/**
 * Source-mix helpers for the Sales Analysis Gross Sales bars.
 * Visualization only — does not recalculate period totals.
 */

export const SOURCE_SALES_KEY_PREFIX = "__src_";
export const FALLBACK_SALES_KEY = "__src_fallback";
export const FALLBACK_SOURCE_LABEL = "Other";

/**
 * High-contrast categorical palette for stacked source segments.
 * Index is taken from the Source filter option list so a given source
 * keeps the same color regardless of how many sources are selected.
 * First hues are distinct families (blue / red / green), then orange,
 * purple, cyan, amber, pink, teal, brown — not adjacent shades of one color.
 */
export const SOURCE_COLOR_PALETTE = [
  "#2563EB", // blue
  "#DC2626", // red
  "#16A34A", // green
  "#EA580C", // orange
  "#7C3AED", // purple
  "#0891B2", // cyan
  "#CA8A04", // amber
  "#DB2777", // pink
  "#0F766E", // teal
  "#92400E", // brown
  "#4F46E5", // indigo
  "#65A30D", // lime
  "#C026D3", // magenta
  "#1D4ED8", // navy
  "#B45309", // rust
];

const roundMoney = (value) =>
  Math.round((Number(value) + Number.EPSILON) * 100) / 100;

export const sourceSalesDataKey = (source) =>
  `${SOURCE_SALES_KEY_PREFIX}${source}`;

export const sourceFromSalesDataKey = (dataKey) => {
  if (typeof dataKey !== "string" || !dataKey.startsWith(SOURCE_SALES_KEY_PREFIX)) {
    return null;
  }
  return dataKey.slice(SOURCE_SALES_KEY_PREFIX.length);
};

const hashString = (value) => {
  let hash = 0;
  const text = String(value);
  for (let i = 0; i < text.length; i += 1) {
    hash = (hash * 31 + text.charCodeAt(i)) | 0;
  }
  return Math.abs(hash);
};

export const getSourceColor = (
  source,
  sourceOptions = [],
  palette = SOURCE_COLOR_PALETTE,
) => {
  if (!palette.length) return "#2563EB";
  const index = sourceOptions.indexOf(source);
  if (index >= 0) return palette[index % palette.length];
  if (!source) return palette[0];
  return palette[hashString(source) % palette.length];
};

/**
 * Sources that actually contribute to at least one month, ordered by the
 * Source filter so legend / stack order stays stable.
 */
export const collectChartSources = (periodHistory = [], sourceOptions = []) => {
  const fromData = new Set();
  periodHistory.forEach((row) => {
    (row.source_breakdown || []).forEach((part) => {
      if (part?.source) fromData.add(part.source);
    });
  });
  const ordered = sourceOptions.filter((source) => fromData.has(source));
  const extras = [...fromData]
    .filter((source) => !sourceOptions.includes(source))
    .sort((a, b) => a.localeCompare(b));
  return [...ordered, ...extras];
};

/**
 * Allocate each source's sales as a stacked segment of the existing
 * monthly total. Segment values always sum to `existingTotal` (the
 * current bar height). Zero totals do not divide.
 */
export const allocateSourceSegments = (breakdown, existingTotal) => {
  const total = Number(existingTotal);
  if (!Number.isFinite(total) || total <= 0) return [];

  const rows = (Array.isArray(breakdown) ? breakdown : [])
    .map((part) => ({
      source: part?.source,
      sales: Number(part?.gross_sales) || 0,
    }))
    .filter((part) => part.source && part.sales > 0);

  const sourceSum = rows.reduce((sum, part) => sum + part.sales, 0);
  if (sourceSum <= 0) return [];

  const allocated = rows.map((part) => {
    const share = part.sales / sourceSum;
    return {
      source: part.source,
      sales: part.sales,
      share,
      value: roundMoney(share * total),
    };
  });

  const allocatedSum = allocated.reduce((sum, part) => sum + part.value, 0);
  const delta = roundMoney(total - allocatedSum);
  if (allocated.length && delta !== 0) {
    const last = allocated[allocated.length - 1];
    last.value = roundMoney(last.value + delta);
  }

  return allocated;
};

/** Remaining height so stacked segments always match the existing total. */
export const leftoverSales = (existingTotal, mix) => {
  const total = Number(existingTotal);
  if (!Number.isFinite(total) || total <= 0) return 0;
  const mixSum = (mix || []).reduce(
    (sum, part) => sum + (Number(part.value) || 0),
    0,
  );
  const leftover = roundMoney(total - mixSum);
  return leftover > 0 ? leftover : 0;
};

export const formatSharePct = (share) => {
  if (share == null || !Number.isFinite(share)) return "—";
  return `${(share * 100).toFixed(1).replace(/\.0$/, "")}%`;
};
